import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getDailyStatePanel,
  getDocumentLink,
} from 'services/unholsterDailyStateService';
import { useSnackbar } from 'context/SnackbarContext';
import { useLanguage } from '../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../utils/createScopedI18n';
import { formatDateByLocale } from '../../../utils/formatDate';
import { dailyStatePanelUnholsterI18n } from './i18n';
import '../home.css';

// Solo para el esqueleto de carga. El panel pide todos los pendientes del dia:
// ya vienen en la misma consulta, limitarlos solo desalinearia el contador.
const FILAS_ESQUELETO = 10;

const DailyStatePanelUnholster = ({
  DesignSystemComponents,
  userId,
  canView,
  onCounters,
}) => {
  const { language } = useLanguage();
  const locale = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: dailyStatePanelUnholsterI18n });
  const { showSnackbar } = useSnackbar();

  const { Card, Button, Badge, IconButton, SvgIcon, Skeleton } =
    DesignSystemComponents;

  const [items, setItems] = useState([]);
  const [alcanceExcedido, setAlcanceExcedido] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const abortControllerRef = useRef(null);
  const onCountersRef = useRef(onCounters);
  onCountersRef.current = onCounters;

  const cargar = useCallback(async () => {
    if (!userId || !canView) return;

    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    setError(null);

    try {
      // El alcance es "mis causas" y el ultimo dia habil: los resuelve el backend con el token.
      const page = await getDailyStatePanel(0, controller.signal);

      setAlcanceExcedido(
        (page?.meta?.advertencias ?? []).includes('alcance_demasiado_amplio')
      );
      setItems(page?.items ?? []);
      onCountersRef.current?.({
        urgentes: page?.contadores?.urgentes ?? 0,
        pendientes: page?.contadores?.pendientes ?? 0,
      });
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;

      setItems([]);
      setError(err);
      onCountersRef.current?.({ urgentes: 0, pendientes: 0 });
    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
  }, [userId, canView]);

  useEffect(() => {
    dailyStatePanelUnholsterI18n.changeLanguage(locale);
  }, [locale]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // El estado diario y Movimientos avisan cuando se gestiona algo.
  useEffect(() => {
    const refrescar = () => cargar();
    window.addEventListener('dailyStateUpdated', refrescar);
    return () => window.removeEventListener('dailyStateUpdated', refrescar);
  }, [cargar]);

  // El Link envuelve la fila, asi que el icono tiene que frenar la navegacion.
  const abrirDocumento = async (e, documentoId) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const link = await getDocumentLink(documentoId);

      if (!link?.disponible) {
        showSnackbar(t('dsp_document_unavailable'), 'warning');
        return;
      }

      window.open(link.url, '_blank', 'noopener,noreferrer');
    } catch {
      showSnackbar(t('dsp_document_error'), 'error');
    }
  };

  const vacio = (titulo, subtitulo) => (
    <>
      <p className='text-center py-4'>{titulo}</p>
      <p className='mini fw-normal font-light text-center'>{subtitulo}</p>
    </>
  );

  return (
    <Card className='card-state overflow-hidden' notPadding>
      <div className='d-flex align-items-center justify-content-between pt-4 px-4'>
        <h4 className='fw-normal me-3'>{t('dsp_title')}</h4>
        {canView && (
          <Link to='/estado-diario' className='text-decoration-none'>
            <Button
              iconEnd='arrowRightShort'
              label={t('dsp_seeAll')}
              size='sm'
              variant='naked'
            />
          </Link>
        )}
      </div>

      <hr className='line mt-4 mb-4' />

      <div className='ps-4 pe-2 pb-4 overflow-auto long-card-content-left'>
        {isLoading &&
          [...Array(FILAS_ESQUELETO)].map((_, i) => (
            <div key={i} className='mb-3'>
              <Skeleton height={60} />
            </div>
          ))}

        {!isLoading && error && (
          <div className='text-center py-4'>
            <p className='mb-2'>{t('dsp_error')}</p>
            <Button
              label={t('dsp_retry')}
              variant='naked'
              size='sm'
              onClick={cargar}
            />
          </div>
        )}

        {!isLoading &&
          !error &&
          alcanceExcedido &&
          vacio(t('dsp_tooManyCases'), t('dsp_tooManyCasesSubtitle'))}

        {!isLoading &&
          !error &&
          !alcanceExcedido &&
          items.length === 0 &&
          vacio(t('dsp_empty'), t('dsp_emptySubtitle'))}

        {!isLoading &&
          !error &&
          items.map((item) => (
            <Link
              key={item.estadoDiarioId}
              to={`/detalle-causa/${item.caseId}/movimientos`}
              state={{
                caseData: {
                  id: item.caseId,
                  code: item.rol,
                  civilCourtCode: item.civilCourtCode,
                  newCivilCourtCode: item.newCivilCourtCode,
                  businessAreaId: item.businessAreaId,
                },
              }}
              className={`container-like-card like-card-left d-flex align-items-center justify-content-between cursor-pointer text-decoration-none ${
                item.nivelPrioridad > 0 ? 'urgent-update' : ''
              }`}
            >
              <div className='d-flex align-items-center flex-grow-1 overflow-hidden'>
                <div className='d-flex flex-column me-4 flex-grow-1 overflow-hidden'>
                  <p className='body mb-2 text-truncate'>{item.titulo}</p>
                  <div className='d-flex align-items-center overflow-hidden'>
                    <div className='d-flex overflow-hidden'>
                      <p className='mini fw-semibold mb-0 flex-shrink-0 me-1 text-nowrap font-light'>
                        {item.tramite || '--'}
                      </p>
                      <p className='mini fw-normal mb-0 text-truncate font-light'>
                        {item.descripcionTramite || ''}
                      </p>
                    </div>
                  </div>
                </div>

                <div className='me-4 flex-shrink-0'>
                  <Badge
                    autoWidth
                    color='neutral'
                    size='sm'
                    text={item.rol}
                    className='mb-2 ms-2'
                  />
                  <div className='d-flex align-items-center'>
                    <SvgIcon
                      className='me-2 flex-shrink-0'
                      size='1rem'
                      name='calendar'
                      stroke='var(--font-light)'
                      fill='var(--font-light)'
                    />
                    <p className='mini fw-normal font-light mb-0'>
                      {formatDateByLocale(item.fecha, locale)}
                    </p>
                  </div>
                </div>
              </div>

              <div className='d-flex align-items-center flex-shrink-0'>
                {item.conDocumento && item.documentoId && (
                  <IconButton
                    ariaLabel={t('dsp_viewDocument')}
                    icon='fileEarmarkText'
                    size='md'
                    onClick={(e) => abrirDocumento(e, item.documentoId)}
                  />
                )}
                <IconButton ariaLabel={t('dsp_go')} icon='chevronRight' size='lg' />
              </div>
            </Link>
          ))}
      </div>
    </Card>
  );
};

export default DailyStatePanelUnholster;
